/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, Slice, PayloadAction } from '@reduxjs/toolkit';

import { OperationStatus, OperationResult } from '../../types';
import ApiError from '../../types/error';
import {
  initialState,
  DevicesInitialState,
  DeviceCategory,
  PostDeviceMappingRequestPayload,
  DeviceType,
  Device,
  DeviceSubType,
  GetDeviceMappingRequestResponse,
  GetDeviceMappingRequestPayload,
  DeviceMappingRequestStatus,
  DeviceMetadata,
  CategoryType
} from '../../types/devices';

import { unmarshalError } from '../../utils/common';
import {
  updateCachedDeviceMapping,
  cleanupExpiredDeviceMappingCache,
  clearCachedDeviceMappingForCategory
} from '../../utils/cached-device-mapping';

function applyDeviceMappingRequestEntry(
  deviceMappingRequest: Record<string, OperationResult>,
  category_type: CategoryType | undefined,
  entry: OperationResult
): void {
  if (category_type) {
    deviceMappingRequest[category_type] = entry;
  }
}

const devicesSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    getDeviceCategoriesRequest: (
      state: DevicesInitialState,
      action: PayloadAction
    ) => {
      state.getDeviceCategories = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    getDeviceCategoriesSuccess: (
      state: DevicesInitialState,
      action: PayloadAction<DeviceCategory[]>
    ) => {
      state.getDeviceCategories = {
        error: null,
        status: OperationStatus.COMPLETED
      };
      state.deviceCategories = action.payload;
    },
    getDeviceCategoriesFailure: (
      state: DevicesInitialState,
      action: PayloadAction<ApiError>
    ) => {
      state.getDeviceCategories = {
        error: action.payload,
        status: OperationStatus.ERRORED
      };
    },
    getDeviceCategoriesCleanup: (state) => {
      state.deviceCategories = initialState.deviceCategories;
      state.getDeviceCategories = initialState.getDeviceCategories;
    },

    postDeviceMappingRequest: (
      state: DevicesInitialState,
      action: PayloadAction<{
        type: string;
        payload: PostDeviceMappingRequestPayload;
      }>
    ) => {
      state.deviceMappingRequest ??= {};
      state.deviceMappingRequest[action.payload.type] = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    postDeviceMappingUpdate: (
      state: DevicesInitialState,
      action: PayloadAction<{
        id: string;
        device_type: DeviceType;
        device_sub_type?: DeviceSubType;
      }>
    ) => {
      const { id, device_type, device_sub_type = '' } = action.payload;
      state.devices ??= {};

      const deviceKey = device_sub_type
        ? `${device_type}_${device_sub_type}`
        : device_type;
      state.devices[deviceKey] = id;
    },
    postDeviceMappingSetRunning: (
      state: DevicesInitialState,
      action: PayloadAction<{
        type: string;
      }>
    ) => {
      state.deviceMappingRequest ??= {};
      state.deviceMappingRequest[action.payload.type] = {
        error: null,
        status: OperationStatus.RUNNING
      };
    },
    postDeviceMappingSuccess: (
      state: DevicesInitialState,
      action: PayloadAction<{
        type: string;
      }>
    ) => {
      state.deviceMappingRequest[action.payload.type] = {
        error: null,
        status: OperationStatus.COMPLETED
      };
    },
    postDeviceMappingFailure: (
      state: DevicesInitialState,
      action: PayloadAction<{ type: string; error: ApiError }>
    ) => {
      state.deviceMappingRequest[action.payload.type] = {
        error: action.payload.error,
        status: OperationStatus.ERRORED
      };
    },
    postDeviceMappingCleanup: (state) => {
      state.postDeviceMappingRequest = initialState.postDeviceMappingRequest;
    },

    getDeviceMappingRequest: (
      state: DevicesInitialState,
      action: PayloadAction<{
        id: string;
        payload: GetDeviceMappingRequestPayload;
      }>
      // eslint-disable-next-line @typescript-eslint/no-empty-function
    ) => {},
    getDeviceMappingSuccess: (
      state: DevicesInitialState,
      action: PayloadAction<{
        data: GetDeviceMappingRequestResponse;
        payload: GetDeviceMappingRequestPayload;
        userEmail?: string;
      }>
    ) => {
      const {
        data,
        payload: { device_type, device_sub_type = '', category_type },
        userEmail
      } = action.payload;
      const status = data.status;
      const key = device_sub_type
        ? `${device_type}_${device_sub_type}`
        : device_type;
      state.devices ??= {} as Record<string, Device[] | string | ApiError>;

      if (
        status === DeviceMappingRequestStatus.IN_PROGRESS ||
        status === DeviceMappingRequestStatus.QUEUED
      ) {
        return;
      }

      if (status === DeviceMappingRequestStatus.FAILED) {
        const message = 'api-errors.devices.device-mapping.generic-error';
        const err = unmarshalError(message);

        if (data.trace_id) {
          err.trace_id = data.trace_id;
        }
        state.devices[key] = err;

        applyDeviceMappingRequestEntry(
          state.deviceMappingRequest,
          category_type,
          {
            error: err,
            status: OperationStatus.ERRORED
          }
        );

        // Invalidate the cache so the next load makes a fresh POST
        // instead of waiting for maxAge to expire
        if (category_type && userEmail) {
          clearCachedDeviceMappingForCategory(category_type, userEmail);
        }
        return;
      }

      if (status !== DeviceMappingRequestStatus.COMPLETED || !data.devices) {
        return;
      }

      // Check if this is the first time we're getting devices (not from cache)
      const isFirstCompletion = !Array.isArray(state.devices[key]);

      applyDeviceMappingRequestEntry(
        state.deviceMappingRequest,
        category_type,
        {
          error: null,
          status: OperationStatus.COMPLETED
        }
      );
      state.devices[key] = data.devices;

      if (data.DeviceMetadata) {
        state.deviceMetadata ??= {} as Record<string, DeviceMetadata[]>;
        state.deviceMetadata[key] = data.DeviceMetadata;
      }

      // Update cache only on first completion (fresh from server, not from browser cache)
      // Subsequent GET polls that return cached responses won't update the cachedAt timestamp
      if (isFirstCompletion && data.maxAge && userEmail && category_type) {
        updateCachedDeviceMapping(
          category_type,
          data.id,
          data.maxAge,
          userEmail
        );
      }

      // Cleanup expired cache entries
      if (userEmail) {
        cleanupExpiredDeviceMappingCache(userEmail);
      }
    },
    getDeviceMappingFailure: (
      state: DevicesInitialState,
      action: PayloadAction<{
        error: ApiError;
        device_type: DeviceType;
        device_sub_type?: DeviceSubType;
      }>
    ) => {
      const { device_type, device_sub_type } = action.payload;
      const deviceKey = device_sub_type
        ? `${device_type}_${device_sub_type}`
        : device_type;
      state.devices ??= {};
      state.devices[deviceKey] = action.payload.error;
    },
    getDeviceMappingCleanup: (state) => {
      state.devices = initialState.devices;
    }
  }
});

export const {
  getDeviceCategoriesRequest,
  getDeviceCategoriesSuccess,
  getDeviceCategoriesFailure,
  getDeviceCategoriesCleanup,

  postDeviceMappingRequest,
  postDeviceMappingUpdate,
  postDeviceMappingSuccess,
  postDeviceMappingFailure,
  postDeviceMappingCleanup,
  postDeviceMappingSetRunning,

  getDeviceMappingRequest,
  getDeviceMappingSuccess,
  getDeviceMappingFailure,
  getDeviceMappingCleanup
} = devicesSlice.actions;

const devicesSliceExport: Slice<DevicesInitialState> = devicesSlice;
export default devicesSliceExport;
